/**
 * Two-pane Appeal Info modal opened from Case Appeals View/Edit.
 */

import { useEffect } from "react";
import { closeModal as closeOasisModal, Modal, openModal } from "risa-oasis-ui_v2";

import { CaseAppealsTableRowData } from "../../../data-model/caseAppealsTable";
import { ModalId } from "../../../enums/modalId";
import AppealInfoPane from "./panes/appealInfoPane";
import CaseDetailsPane from "./panes/caseDetailsPane";

interface AppealInfoModalProps {
  data: CaseAppealsTableRowData;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Two-pane modal matching the Non-EPA Add/Edit Summary layout.
 * Left pane: read-only case details and appeal history.
 * Right pane: a single Appeal Info form (no Call/Fax tabs).
 *
 * @param props.data - Appeal table row the modal was opened for
 * @param props.onClose - Clears the opened modal in redux
 * @param props.onSuccess - Called after a successful save so the table refetches
 * @returns Appeal Info modal element
 */
const AppealInfoModal = (props: AppealInfoModalProps) => {
  const title =
    props.data?.appeal_status != null ? "View Appeal Info" : "Edit Appeal Info";

  useEffect(() => {
    openModal(ModalId.APPEAL_INFO_MODAL);
  }, []);

  const handleClose = () => {
    closeOasisModal(ModalId.APPEAL_INFO_MODAL);
    props.onClose();
  };

  return (
    <Modal
      dialogId={ModalId.APPEAL_INFO_MODAL}
      title={title}
      onClose={props.onClose}
      onCancel={handleClose}
      onSave={handleClose}
      saveButtonText="Save"
      cancelText="Cancel"
      hideFooter
      heightPercentage={88}
    >
      {/* `w-screen max-w-full` widens the dialog to the 70% viewport cap set by
          the shared `dialog.custom-modal.show` rule without overflowing it. */}
      <div className="flex h-full w-screen max-w-full py-3">
        <div className="flex h-full min-h-0 w-full overflow-hidden rounded-lg border border-primaryGray-15">
          <div className="w-2/5 min-w-0 shrink-0 border-r border-primaryGray-15">
            <CaseDetailsPane data={props.data} />
          </div>
          <div className="min-w-0 flex-1">
            <AppealInfoPane
              key={props.data.identifier}
              data={props.data}
              onClose={handleClose}
              onSuccess={props.onSuccess}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AppealInfoModal;
